import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  OutputRefSubscription,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  finalize,
  from,
  mergeMap,
  of,
  Subscriber,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  DeviceDetectorService,
  FileDownloadService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestService } from '@core/services/request/request.service';
import { environment } from '@environment/environment';
import {
  tuiScrollbarOptionsProvider,
  TuiExpand,
  TuiScrollbar,
  TuiDropdown,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiDialogService,
} from '@taiga-ui/core';
import { TuiBadge, TuiProgress, TuiSkeleton } from '@taiga-ui/kit';
import { Counterparty, RequestFormFilesTranslations } from '@core/types';
import type {
  RequestFile,
  RequestFileCategory,
  RequestFormFilesTranslationsPayload,
} from '@core/types';
import { isImageByFilename } from '@core/helpers';
import type { Nullable } from '@core/helpers';
import { RequestFilesPersonType } from '../../types';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { AccountService } from '@core/services/account/account.service';
import { ImagePreviewComponent } from '@core/components/image-preview/image-preview.component';
import { FilePreviewComponent } from '@core/components/file-preview/file-preview.component';
import type { FileGroup } from '@features/requests/request-form/components/request-form-summary/types';
import { EmptyBlockComponent } from '@core/components/empty-block/empty-block.component';
import { FileExtensionPipe } from '@core/pipes/file-extension.pipe';
import { FilePreviewUrlPipe } from '@core/pipes/file-preview-url.pipe';
import { OutkeepPermission } from '@outkeep/shared';

@Component({
  selector: 'x-request-form-files-common',
  imports: [
    TuiSkeleton,
    CommonModule,
    TuiButton,
    TuiIcon,
    TuiHint,
    TuiExpand,
    TuiDropdown,
    TranslatePipe,
    TuiProgress,
    ImagePreviewComponent,
    TuiScrollbar,
    TuiBadge,
    EmptyBlockComponent,
    FilePreviewComponent,
    FilePreviewUrlPipe,
    FileExtensionPipe,
  ],
  templateUrl: './request-form-files-common.component.html',
  styleUrl: './request-form-files-common.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    tuiScrollbarOptionsProvider({
      mode: 'always',
    }),
  ],
})
export class RequestFormFilesCommonComponent implements OnInit, OnDestroy {
  $filesInput = input<Nullable<RequestFile[]>>(null, {
    alias: 'files',
  });

  $fileCatsInput = input<Nullable<RequestFileCategory[]>>(null, {
    alias: 'fileCats',
  });

  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  $showFileModal = input<OutputEmitterRef<void>>(undefined, {
    alias: 'showFileModal',
  });

  $renderModalLayoutInline = input<boolean>(false, {
    alias: 'renderModalLayoutInline',
  });

  readonly requestFilesPersonType = input.required<RequestFilesPersonType>();

  public $showPreviewImage = output<[RequestFile, FileGroup, index: number]>();

  private readonly requestService = inject(RequestService);
  private readonly accountService = inject(AccountService);
  private readonly xAlertService = inject(XAlertService);
  private readonly requestFilesService = inject(RequestFilesService);
  public readonly deviceService = inject(DeviceDetectorService);
  private readonly dialogService$ = inject(TuiDialogService);

  protected requestFiles: RequestFile[] = [];
  private filesByCats: Record<RequestFileCategory['id'], RequestFile[]> = {};

  private readonly destroyRef = inject(DestroyRef);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  public isInitialLoad = true;

  readonly permissionService = inject(OutkeepPermissionService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly fileDownloadService = inject(FileDownloadService);
  readonly $request = this.requestService.$request;
  readonly $currentUserCounterparties = toSignal(
    this.accountService
      .getCurrentUserCounterparties$()
      .pipe(catchError(() => of([] as Counterparty[]))),
    {
      initialValue: [] as Counterparty[],
    },
  );

  public fileCats: RequestFileCategory[] = [];

  public fileCatsExpanded: Record<number, boolean> = {};

  public hoveredFileCatId: Nullable<number> = null;

  public hoveredFileId: Nullable<number> = null;

  public $filesUploadingTotalByCat = signal<
    Record<RequestFileCategory['id'], number>
  >({});
  public $filesProcessedCountByCat = signal<
    Record<RequestFileCategory['id'], number>
  >({});

  private readonly canEditRequestsOfAnyContractor =
    this.permissionService.$hasPermission(
      OutkeepPermission.LogistEditRequestsOfAnyContractor,
    );

  private readonly hasUploadFilesPermission =
    this.permissionService.$hasPermission(OutkeepPermission.LogistFilesUpload);

  private readonly hasOnlyOwnerUploadFilesPermission =
    this.permissionService.$hasPermission(
      OutkeepPermission.LogistOnlyOwnerFilesUpload,
    );

  private readonly hasOnlyContractorUploadFilesPermission =
    this.permissionService.$hasPermission(
      OutkeepPermission.LogistOnlyContractorFilesUpload,
    );

  readonly $canUploadFiles = computed(() => {
    if (this.canEditRequestsOfAnyContractor()) {
      return true;
    }

    const all = this.hasUploadFilesPermission();
    const onlyClient = this.hasOnlyOwnerUploadFilesPermission();
    const onlyContractor = this.hasOnlyContractorUploadFilesPermission();

    const counterparties =
      all || onlyClient || onlyContractor
        ? this.$currentUserCounterparties()
        : [];
    const request = this.$request();
    const isInClientCounterparty = this.currentUserCounterpartyExists(
      counterparties,
      request.counterpartyOwnerId?.id,
    );
    const isInInstallerCounterparty = this.currentUserCounterpartyExists(
      counterparties,
      request.contractorCounterpartyId?.id,
    );

    const hasClientPermission = all || onlyClient;
    const hasContractorPermission = all || onlyContractor;

    return (
      (this.requestFilesPersonType() === RequestFilesPersonType.CLIENT &&
        hasClientPermission &&
        isInClientCounterparty) ||
      (this.requestFilesPersonType() === RequestFilesPersonType.INSTALLER &&
        hasContractorPermission &&
        isInInstallerCounterparty)
    );
  });

  private translates: RequestFormFilesTranslationsPayload = {
    [RequestFormFilesTranslations.ErrorUploadingFile]: 'Ошибка загрузки файла',
    [RequestFormFilesTranslations.SuccessUploadingFile]:
      'Файл успешно загружен',
    [RequestFormFilesTranslations.SuccessUploadingAllFiles]:
      'Все файлы успешно загружены',
    [RequestFormFilesTranslations.ErrorSavingFile]: 'Ошибка сохранения файла',
    [RequestFormFilesTranslations.DialogDeleteFile]:
      'Вы действительно хотите удалить файл ',
    [RequestFormFilesTranslations.DialogDeleteFileTitle]: 'Удаление файла',
    [RequestFormFilesTranslations.SuccessDeletingFile]: 'Файл успешно удален',
    [RequestFormFilesTranslations.ErrorDeletingFile]: 'Ошибка удаления файла',
    [RequestFormFilesTranslations.DialogRequestToCancelDeleteTitle]:
      'Запрос на отмену удаления файла',
    [RequestFormFilesTranslations.DialogRequestToCancelDeleteMsg]:
      'Вы действительно хотите отменить заявку на удаление файла',
    [RequestFormFilesTranslations.DialogRequestToDeleteTitle]:
      'Запрос на удаление файла',
    [RequestFormFilesTranslations.DialogRequestToDeleteMsg1]:
      'Вы действительно хотите создать заявку на удаление файла',
    [RequestFormFilesTranslations.DialogRequestToDeleteMsg2]:
      'Будет создан запрос на удаление файла. После одобрение администратором файл будет удален.',
    [RequestFormFilesTranslations.SuccessRequestToCancelDelete]:
      'Заявка на отмену удаления файла успешно создана',
    [RequestFormFilesTranslations.SuccessRequestToDelete]:
      'Заявка на удаление файла успешно создана',
    [RequestFormFilesTranslations.ErrorRequestToCancelDelete]:
      'Ошибка создания запроса на отмену удаления файла',
    [RequestFormFilesTranslations.ErrorRequestToDelete]:
      'Ошибка создания запроса на удаление файла',
    [RequestFormFilesTranslations.VinCopied]: 'VIN скопирован в буфер обмена',
    [RequestFormFilesTranslations.ErrorCopiedVin]:
      'Ошибка копирования VIN в буфер обмена',
    [RequestFormFilesTranslations.DialogDeleteAllCatFilesTitle]:
      'Вы действительно хотите удалить все файлы из этой категории?',
    [RequestFormFilesTranslations.SuccessDeletingAllCatFiles]:
      'Файлы категории успешно удалены',
    [RequestFormFilesTranslations.ErrorDeletingAllCatFiles]:
      'Ошибка удаления файлов категории',
  };

  public $catsUploadProgress = signal<
    Record<RequestFileCategory['id'], number>
  >({});

  public $catUploadInProgress = signal<
    Record<RequestFileCategory['id'], boolean>
  >({});

  private showFileModalEmitterSub: Nullable<OutputRefSubscription> = null;

  protected readonly RequestFilesPersonType = RequestFilesPersonType;

  @ViewChild('allFilesModalTemplate')
  allFilesModalTemplate!: TemplateRef<any>;

  constructor() {
    effect(() => {
      const isInsideSummary = this.$isInsideSummary();

      if (!isInsideSummary) {
        return;
      }

      const requestFiles = this.$filesInput();
      const fileCats = this.$fileCatsInput();

      if (requestFiles && fileCats) {
        this.parseFiles(requestFiles, fileCats);
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
      }
    });
    effect(() => {
      const showFileModalEmitter = this.$showFileModal();

      if (showFileModalEmitter) {
        this.showFileModalEmitterSub = showFileModalEmitter.subscribe(() => {
          this.openFilesModal();
        });
      }
    });
  }

  ngOnInit(): void {
    if (!this.$isInsideSummary()) {
      this.getInitialFilesRequests();
    }
    this.getTranslations();
  }

  ngOnDestroy(): void {
    if (this.showFileModalEmitterSub) {
      this.showFileModalEmitterSub.unsubscribe();
      this.showFileModalEmitterSub = null;
    }
  }

  private getInitialFilesRequests() {
    this.requestService.request$
      .pipe(
        switchMap(request =>
          combineLatest({
            requestFiles: this.requestFilesService.entities$,
            fileCats: this.requestFilesService.getRequestFileCategories$(
              request.counterpartyOwnerId?.id
                ? [request.counterpartyOwnerId.id]
                : [],
            ),
          }),
        ),
        tap(() => {
          if (this.isInitialLoad) {
            this.isInitialLoad = false;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ requestFiles, fileCats }) => {
        this.parseFiles(requestFiles, fileCats);
      });
  }

  private openFilesModal() {
    this.dialogService$
      .open(this.allFilesModalTemplate, {
        size: null,
        appearance: 'taiga desktop-all-files-cat-modal',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private parseFiles(
    requestFiles: RequestFile[],
    fileCats: RequestFileCategory[],
  ) {
    this.requestFiles = requestFiles;
    this.fileCats = fileCats.filter(this.isRelevantCat.bind(this));
    this.fileCatsExpanded = this.fileCats.reduce(
      (acc, cat) => ({
        ...acc,
        [cat.id]: acc[cat.id] || false,
      }),
      this.fileCatsExpanded,
    );

    this.filesByCats = fileCats.reduce(
      (acc, cat) => ({
        ...acc,
        [cat.id]: this.requestFiles.filter(
          file => (file.catId as RequestFileCategory)?.id === cat.id,
        ),
      }),
      {},
    );

    this.$catsUploadProgress.update(
      (current: Record<RequestFileCategory['id'], number>) =>
        this.fileCats.reduce(
          (acc, cat) => ({
            ...acc,
            [cat.id]: acc[cat.id] ?? 0,
          }),
          current,
        ),
    );
  }

  private getTranslations() {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormFilesTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  private isRelevantCat(cat: RequestFileCategory): boolean {
    return this.requestFilesPersonType() === RequestFilesPersonType.CLIENT
      ? !cat.isContractorFileCat
      : cat.isContractorFileCat;
  }

  expandedNoFileCats = false;

  private currentUserCounterpartyExists(
    counterparties: Counterparty[],
    counterpartyId: Counterparty['id'] | undefined,
  ): boolean {
    return !!(
      counterpartyId && counterparties.some(it => it.id === counterpartyId)
    );
  }

  @pure
  getFiles(catId: RequestFileCategory['id']): RequestFile[] {
    return this.filesByCats[catId] || [];
  }
  @pure
  getFileCat(catId: RequestFileCategory | number) {
    if (typeof catId !== 'number') {
      return catId;
    }

    return this.fileCats.find(cat => cat.id === catId);
  }

  @pure
  filesExist(catId: RequestFileCategory['id']) {
    return this.filesByCats[catId]?.length > 0;
  }

  @pure
  catsWithOneDocFile(cats: RequestFileCategory[]): RequestFileCategory[] {
    if (cats.length === 0) {
      return [];
    }

    return cats.filter(
      cat =>
        this.getFiles(cat.id).length === 1 &&
        !this.fileIsImage(this.getFiles(cat.id)[0]),
    );
  }

  @pure
  catsWithMoreThanOneFileOrImages(
    cats: RequestFileCategory[],
  ): RequestFileCategory[] {
    if (cats.length === 0) {
      return [];
    }

    return cats.filter(cat => {
      const files = this.getFiles(cat.id);

      return files.length > 1 || files.some(this.fileIsImage);
    });
  }

  @pure
  fileIsImage(file: RequestFile) {
    return isImageByFilename(file.filename);
  }

  @pure
  fileIsRequestedToDelete(file: RequestFile) {
    return file.deleteRequestUserId !== null;
  }

  @pure
  getFileUrl(file: RequestFile) {
    return environment.staticUrl + file.path;
  }

  @pure
  getDownloadFileUrl(path: RequestFile['path']) {
    return environment.downloadFilesUrl + path;
  }

  downloadFile(file: RequestFile) {
    if (file.filename.toLowerCase().endsWith('.pdf')) {
      this.fileDownloadService.openPdfByPath(file.path, file.filename);

      return;
    }

    this.createLinkAndClick(
      this.getDownloadFileUrl(file.path),
      file.filename,
      false,
    );
  }

  onFileChanged(event: Event, fileCat: RequestFileCategory): void {
    const element = event.currentTarget as HTMLInputElement;

    if (!element.files || !element.files.length) {
      return;
    }
    this.uploadFiles(Array.from(element.files), fileCat);
  }

  onDrop(event: DragEvent, fileCat: RequestFileCategory) {
    this.eventPrevent(event);
    const element = event.currentTarget as HTMLElement;

    this.renderer.removeClass(element, 'drag-over');
    const files = event.dataTransfer?.files;

    if (!files) {
      return;
    }

    this.uploadFiles(Array.from(files), fileCat);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    this.eventPrevent(event);
    const element = event.currentTarget as HTMLElement;

    this.renderer.addClass(element, 'drag-over');
  }

  @HostListener('dragleave', ['$event'])
  @HostListener('drop', ['$event'])
  onDragLeave(event: DragEvent) {
    this.eventPrevent(event);
    const element = event.currentTarget as HTMLElement;

    this.renderer.removeClass(element, 'drag-over');
  }

  eventPrevent(evt: Event) {
    evt.preventDefault();
    evt.stopPropagation();
  }

  private async uploadFiles(files: File[], fileCat: RequestFileCategory) {
    if (!files?.length) {
      return;
    }

    if (this.$catUploadInProgress()[fileCat.id]) {
      return;
    }

    const req = this.$request();

    if (!req) {
      return;
    }

    // На iOS (WebKit) ссылка на File иногда инвалидируется между выбором
    // (например, съёмка фото камерой из <input type="file">, из-за чего
    // вкладка уходит в фон) и отправкой - в итоге читается 0 байт.
    // Материализуем содержимое в памяти сразу при выборе, чтобы дальше
    // грузить уже независимую от исходной ссылки копию байтов.
    const materializedFiles = await Promise.all(
      files.map(async file => {
        try {
          const buffer = await file.arrayBuffer();

          return new File([buffer], file.name, { type: file.type });
        } catch {
          return file;
        }
      }),
    );

    const emptyFiles = materializedFiles.filter(file => file.size === 0);

    emptyFiles.forEach(file => {
      this.xAlertService.error(
        this.translates[RequestFormFilesTranslations.ErrorUploadingFile],
        `${file.name}: файл пустой, попробуйте выбрать его заново`,
      );
    });

    files = materializedFiles.filter(file => file.size > 0);

    if (!files.length) {
      return;
    }

    const totalFiles = files.length;
    const fileProgresses: number[] = new Array(totalFiles).fill(0);
    let uploadedFilesCount = 0;
    let hadErrors = false;

    const updateOverallProgress = () => {
      const overallProgress = Math.min(
        Math.round(
          fileProgresses.reduce((sum, progress) => sum + progress, 0) /
            fileProgresses.length,
        ),
        100,
      );

      this.$catsUploadProgress.update(
        (current: Record<RequestFileCategory['id'], number>) => ({
          ...current,
          [fileCat.id]: overallProgress,
        }),
      );
    };

    this.$catsUploadProgress.update(
      (current: Record<RequestFileCategory['id'], number>) => ({
        ...current,
        [fileCat.id]: 0,
      }),
    );
    this.$catUploadInProgress.update(
      (current: Record<RequestFileCategory['id'], boolean>) => ({
        ...current,
        [fileCat.id]: true,
      }),
    );
    this.$filesUploadingTotalByCat.update(
      (current: Record<RequestFileCategory['id'], number>) => ({
        ...current,
        [fileCat.id]: totalFiles,
      }),
    );
    this.$filesProcessedCountByCat.update(
      (current: Record<RequestFileCategory['id'], number>) => ({
        ...current,
        [fileCat.id]: 0,
      }),
    );

    from(files)
      .pipe(
        mergeMap(
          (file, index) =>
            this.requestFilesService
              .uploadRequestFile$(req.id, req.requestNumber, fileCat, file)
              .pipe(
                tap(({ progress }) => {
                  fileProgresses[index] = progress;
                  updateOverallProgress();
                }),
                mergeMap(({ response }) =>
                  response
                    ? this.requestFilesService
                        .saveRequestFileName$(
                          req.id,
                          fileCat.id,
                          response,
                          file.name,
                        )
                        .pipe(
                          tap(() => {
                            uploadedFilesCount++;
                            this.xAlertService.success(
                              this.translates[
                                RequestFormFilesTranslations
                                  .SuccessUploadingFile
                              ],
                              file.name,
                            );
                          }),
                          catchError(err => {
                            hadErrors = true;
                            this.xAlertService.error(
                              this.translates[
                                RequestFormFilesTranslations.ErrorSavingFile
                              ],
                              file.name,
                            );
                            console.error('File save name failed', err);

                            return EMPTY;
                          }),
                        )
                    : EMPTY,
                ),
                tap(() => {
                  if (
                    this.requestFilesService.mainRequestFilesRenamePrefixes.includes(
                      fileCat.fileRenamePrefix,
                    )
                  ) {
                    this.requestFilesService.requestsMainFilesWereUpdated$.next();
                  }
                }),
                catchError(err => {
                  hadErrors = true;
                  fileProgresses[index] = 0;
                  updateOverallProgress();
                  this.xAlertService.error(
                    this.translates[
                      RequestFormFilesTranslations.ErrorUploadingFile
                    ],
                    file.name,
                  );
                  console.error('File upload failed', err);

                  return EMPTY;
                }),
                finalize(() => {
                  this.$filesProcessedCountByCat.update(
                    (current: Record<RequestFileCategory['id'], number>) => ({
                      ...current,
                      [fileCat.id]: (current[fileCat.id] ?? 0) + 1,
                    }),
                  );
                }),
              ),
          10,
        ),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.$catsUploadProgress.update(
            (current: Record<RequestFileCategory['id'], number>) => ({
              ...current,
              [fileCat.id]: 0,
            }),
          );
          this.$catUploadInProgress.update(
            (current: Record<RequestFileCategory['id'], boolean>) => ({
              ...current,
              [fileCat.id]: false,
            }),
          );
          this.$filesUploadingTotalByCat.update(
            (current: Record<RequestFileCategory['id'], number>) => ({
              ...current,
              [fileCat.id]: 0,
            }),
          );
          this.$filesProcessedCountByCat.update(
            (current: Record<RequestFileCategory['id'], number>) => ({
              ...current,
              [fileCat.id]: 0,
            }),
          );
          this.requestFilesService.refreshEntities();

          if (!hadErrors && uploadedFilesCount === totalFiles) {
            this.xAlertService.success(
              this.translates[
                RequestFormFilesTranslations.SuccessUploadingAllFiles
              ],
            );
          }
        }),
      )
      .subscribe();
  }

  deleteFile(file: RequestFile, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const msg = `
        ${
          this.translates[RequestFormFilesTranslations.DialogDeleteFile]
        } <b>"${file.filename}</b>?
        `;

    this.xAlertService
      .showDialog(
        this.translates[RequestFormFilesTranslations.DialogDeleteFileTitle],
        msg,
      )
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.requestFilesService
          .deleteRequestFile$(file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.requestFilesService.refreshEntities();
              this.xAlertService.success(
                this.translates[
                  RequestFormFilesTranslations.SuccessDeletingFile
                ],
              );
            },
            error: err => {
              this.xAlertService.error(
                this.translates[RequestFormFilesTranslations.ErrorDeletingFile],
              );
              this.requestFilesService.refreshEntities();
              console.error('File delete failed', err);
            },
          });
      });
  }

  deleteAllCatFiles(cat: RequestFileCategory): void {
    const files = this.getFiles(cat.id);

    if (files.length === 0) {
      return;
    }

    this.xAlertService
      .showDialog(
        this.translates[
          RequestFormFilesTranslations.DialogDeleteAllCatFilesTitle
        ],
        '',
      )
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          return;
        }
        from(files)
          .pipe(
            mergeMap(file =>
              this.requestFilesService.deleteRequestFile$(file).pipe(
                catchError(err => {
                  this.xAlertService.error(
                    this.translates[
                      RequestFormFilesTranslations.ErrorDeletingFile
                    ],
                  );
                  console.error('File delete failed', err);

                  return EMPTY;
                }),
              ),
            ),
            toArray(),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              this.requestFilesService.refreshEntities();
              this.xAlertService.success(
                this.translates[
                  RequestFormFilesTranslations.SuccessDeletingAllCatFiles
                ],
              );
            },
            error: err => {
              this.xAlertService.error(
                this.translates[
                  RequestFormFilesTranslations.ErrorDeletingAllCatFiles
                ],
              );
              console.error('Delete all files in category failed', err);
            },
          });
      });
  }

  createFileDeleteRequest(
    file: RequestFile,
    imagePreviewObserver?: Subscriber<any>,
  ): void {
    const restore = this.fileIsRequestedToDelete(file);
    let msg: string;
    let title: string;

    if (restore) {
      title =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToCancelDeleteTitle
        ];
      msg =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToCancelDeleteMsg
        ] + `<b>"${file.filename}</b>?`;
    } else {
      title =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToDeleteTitle
        ];
      msg =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToDeleteMsg1
        ] +
        `<b>"${file.filename}</b>"? ` +
        this.translates[RequestFormFilesTranslations.DialogRequestToDeleteMsg2];
    }

    this.xAlertService
      .showDialog(title, msg)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.requestFilesService
          .createFileDeleteRequest$(this.$request().id, file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.xAlertService.success(
                restore
                  ? this.translates[
                      RequestFormFilesTranslations.SuccessRequestToCancelDelete
                    ]
                  : this.translates[
                      RequestFormFilesTranslations.SuccessRequestToDelete
                    ],
              );
              this.requestFilesService.refreshEntities();
              imagePreviewObserver?.complete();
            },
            error: err => {
              let msg: string;

              if (restore) {
                msg =
                  this.translates[
                    RequestFormFilesTranslations.ErrorRequestToCancelDelete
                  ];
              } else {
                msg =
                  this.translates[
                    RequestFormFilesTranslations.ErrorRequestToDelete
                  ];
              }
              this.xAlertService.error(msg);
              console.error('File delete request failed', err);
              imagePreviewObserver?.complete();
            },
          });
      });
  }

  expandFileCatsWithoutFile(): void {
    this.expandedNoFileCats = !this.expandedNoFileCats;
  }

  showImagePreview(
    file: RequestFile,
    fileCatId: RequestFileCategory['id'],
    index: number,
  ) {
    const fileCat = this.fileCats.find(c => c.id === fileCatId)!;

    if (!fileCat) {
      this.xAlertService.error('File category not found');

      return;
    }
    const group: FileGroup = {
      name: fileCat.name,
      id: fileCatId,
      filesExists: this.filesExist(fileCat!.id),
      multi: this.isPhotoCat(fileCat!.fileRenamePrefix),
      files: this.getFiles(fileCat!.id),
    };

    this.$showPreviewImage.emit([file, group, index]);
  }

  private createLinkAndClick(
    href: string,
    filename: string,
    withoutDownload = false,
  ): void {
    this.fileDownloadService.downloadFile(
      href,
      filename,
      withoutDownload,
      this.renderer,
    );
  }

  fileBlockClick(event: Event, fileInput: HTMLInputElement) {
    const el = event.target as HTMLElement;

    if (
      el.tagName === 'SECTION' ||
      el.tagName === 'TUI_ISLAND' ||
      el.tagName === 'HR'
    ) {
      fileInput.click();
    }
  }

  @pure
  public isImportantCat(catId: RequestFileCategory['id']): boolean {
    const importantCatsIds: number[] = [21, 15, 18, 17, 31];

    return (
      this.requestFilesPersonType() === RequestFilesPersonType.CLIENT &&
      importantCatsIds.includes(catId)
    );
  }

  @pure
  public isPhotoCat(
    catFileRenamePrefix: RequestFileCategory['fileRenamePrefix'],
  ) {
    return catFileRenamePrefix.startsWith('photo_');
  }

  @pure
  public isScanCat(
    catFileRenamePrefix: RequestFileCategory['fileRenamePrefix'],
  ) {
    return catFileRenamePrefix.startsWith('scan_');
  }

  @pure
  public filterOtherFiles(cat: RequestFileCategory): boolean {
    return (
      !this.isPhotoCat(cat.fileRenamePrefix) &&
      !this.isScanCat(cat.fileRenamePrefix) &&
      !this.isImportantCat(cat.id)
    );
  }

  @pure
  public filterNonImportantFiles(catId: RequestFileCategory['id']): boolean {
    return !this.isImportantCat(catId);
  }

  @pure
  public getFileCatValue(
    fileCat: RequestFileCategory,
    key: keyof RequestFileCategory,
  ) {
    return fileCat[key];
  }
}
